/**
 * 拖拽功能 Hook
 * 处理树节点的拖拽操作
 */

import { useState, useCallback, useRef } from 'react';
import React from 'react';
import { OnDropInfo } from '../iTree';
import { isInclude, getOffset } from '../utils/util';

interface UseDragDropProps {
    draggable?: boolean;
    dragInitialState?: boolean;
    dragDelayExpandTime?: number;
    expandedKeys: string[];
    onDragStart?: (info: { event: React.DragEvent; node: React.ReactElement }) => void;
    onDragEnter?: (info: { event: React.DragEvent; node: React.ReactElement; expandedKeys: string[]; dropPosition: number }) => void;
    onDragOver?: (info: { event: React.DragEvent; node: React.ReactElement; extension: { dropPosition: string; dropPositionX: string } }) => void;
    onDragLeave?: (info: { event: React.DragEvent; node: React.ReactElement }) => void;
    onDrop?: (info: OnDropInfo) => void;
    onDragEnd?: (info: { event: React.DragEvent; node: React.ReactElement }) => void;
    unsetDropPositionX?: boolean;
    children?: React.ReactNode;
    setExpandedKeys: (keys: string[]) => void;
    getExpandedKeys: (treeNode: React.ReactElement, expand: boolean) => string[] | undefined;
    getRawExpandedKeys: () => string[] | undefined;
    flatKeyTree?: string[];
    treeElsObj?: { [key: string]: React.ReactElement };
}

interface UseDragDropReturn {
    dragOverNodeKey: string;
    dropNodeKey: string;
    dropPositionX: string;
    dropPositionY: number | undefined;
    dragNodeHighlight: boolean;
    handleDragStart: (e: React.DragEvent, treeNode: React.ReactElement) => void;
    handleDragEnterGap: (e: React.DragEvent, treeNode: any) => void;
    handleDragEnter: (e: React.DragEvent, treeNode: React.ReactElement) => void;
    handleDragOver: (e: React.DragEvent, treeNode: any, treeNodeContentWrapper: any) => void;
    handleDragLeave: (e: React.DragEvent, treeNode: React.ReactElement) => void;
    handleDrop: (e: React.DragEvent, treeNode: React.ReactElement) => void;
    handleDragEnd: (e: React.DragEvent, treeNode: React.ReactElement) => void;
    getDragNodes: (treeNode: React.ReactElement) => string[];
}

const dropMap: { [name: string]: number } = {
    center: 1,
    right: 0,
    left: 1
};

/**
 * 拖拽功能 Hook
 */
export function useDragDrop(props: UseDragDropProps): UseDragDropReturn {
    const {
        dragInitialState = false,
        dragDelayExpandTime = 500,
        expandedKeys,
        onDragStart,
        onDragEnter,
        onDragOver,
        onDragLeave,
        onDrop,
        onDragEnd,
        unsetDropPositionX = false,
        children,
        setExpandedKeys,
        getExpandedKeys,
        getRawExpandedKeys,
        flatKeyTree = [],
        treeElsObj = {}
    } = props;

    // State
    const [dragOverNodeKey, setDragOverNodeKey] = useState<string>('');
    const [dropNodeKey, setDropNodeKey] = useState<string>('');
    const [dropPositionX, setDropPositionX] = useState<string>('');
    const [dropPositionY, setDropPositionY] = useState<number | undefined>(undefined);
    const [dragNodeHighlight, setDragNodeHighlight] = useState<boolean>(false);

    // Refs
    const dragNodeRef = useRef<React.ReactElement | null>(null);
    const dragNodesKeysRef = useRef<string[]>([]);
    const dropPositionYRef = useRef<number | undefined>(undefined);
    const dragXRef = useRef<number | null>(null);
    const betweenWithKeyRef = useRef<string[]>([]);
    const timerRef = useRef<ReturnType<typeof setTimeout> | undefined>(undefined);
    const dropTriggerRef = useRef<boolean>(true);
    const rawExpandedKeysRef = useRef<string[] | undefined>(undefined);

    /**
     * 获取拖拽的节点keys
     */
    const getDragNodes = useCallback((treeNode: React.ReactElement): string[] => {
        const dragNodesKeys: string[] = [];
        const tPArr = treeNode.props.pos.split('-');

        loopAllChildren(children as React.ReactElement[], (_item: React.ReactElement, _index: number, pos: string, newKey: string) => {
            const pArr = pos.split('-');
            if (treeNode.props.pos === pos || (tPArr.length < pArr.length && isInclude(tPArr, pArr))) {
                dragNodesKeys.push(newKey);
            }
        });

        return dragNodesKeys;
    }, [children]);

    /**
     * 处理拖拽开始
     */
    const handleDragStart = useCallback((e: React.DragEvent, treeNode: React.ReactElement) => {
        e.stopPropagation();
        dragNodeRef.current = treeNode;
        dragNodesKeysRef.current = getDragNodes(treeNode);
        setDragNodeHighlight(true);

        if (!dragInitialState) {
            const expandedKeysForDrag = getExpandedKeys(treeNode, false);
            if (expandedKeysForDrag) {
                rawExpandedKeysRef.current = getRawExpandedKeys();
                setExpandedKeys(expandedKeysForDrag);
            }
        }

        onDragStart?.({
            event: e,
            node: treeNode,
        });

        dropTriggerRef.current = false;

        try {
            e?.dataTransfer?.setData('text/plain', '');
        } finally {
            // empty
        }
    }, [dragInitialState, getDragNodes, getExpandedKeys, getRawExpandedKeys, setExpandedKeys, onDragStart]);

    /**
     * 处理拖拽进入间隙
     */
    const handleDragEnterGap = useCallback((e: React.DragEvent, treeNode: any) => {
        if (!treeNode.treeNodeContentWrapper) return;

        const offsetTop = getOffset(treeNode.treeNodeContentWrapper).top;
        const offsetHeight = treeNode.treeNodeContentWrapper.offsetHeight;
        const pageY = e.pageY || 0;
        const gapHeight = 8;

        // 处理 hover treenode 下半部分
        if (pageY > offsetTop + offsetHeight - gapHeight) {
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
            const keyIndex = flatKeyTree.indexOf(treeNode.props.eventKey);

            if (betweenWithKeyRef.current[1] !== flatKeyTree[keyIndex + 1] ||
                betweenWithKeyRef.current[0] !== treeNode.props.eventKey) {

                // 处理未展开的情况
                if (flatKeyTree[keyIndex + 1] &&
                    treeElsObj[flatKeyTree[keyIndex + 1]]?.props.pos.length >
                    treeElsObj[flatKeyTree[keyIndex]]?.props.pos.length) {

                    if (expandedKeys.includes(flatKeyTree[keyIndex])) {
                        betweenWithKeyRef.current = [treeNode.props.eventKey, flatKeyTree[keyIndex + 1]];
                    } else {
                        let betweenWithKey1 = '';
                        for (let i = keyIndex + 1; i < flatKeyTree.length; i++) {
                            const element = flatKeyTree[i];
                            if (treeElsObj[element]?.props.pos.length <=
                                treeElsObj[flatKeyTree[keyIndex]]?.props.pos.length) {
                                betweenWithKey1 = element;
                                break;
                            }
                        }
                        betweenWithKeyRef.current = [treeNode.props.eventKey, betweenWithKey1];
                    }
                } else {
                    betweenWithKeyRef.current = [treeNode.props.eventKey, flatKeyTree[keyIndex + 1]];
                }
                dragXRef.current = e.pageX;
            }
            dropPositionYRef.current = 1;
            setDropPositionY(1);

            // 在最后一个节点下面的时候失去插入位置
            if (pageY > offsetTop + offsetHeight - 2 && !betweenWithKeyRef.current[1]) {
                betweenWithKeyRef.current = [];
                dropPositionYRef.current = undefined;
                setDropPositionY(undefined);
            }
        } else if (pageY < offsetTop + gapHeight) { // 处理 hover treenode 上半部分
            clearTimeout(timerRef.current);
            timerRef.current = undefined;
            const keyIndex = flatKeyTree.indexOf(treeNode.props.eventKey);

            if (betweenWithKeyRef.current[0] !== flatKeyTree[keyIndex - 1] ||
                betweenWithKeyRef.current[1] !== treeNode.props.eventKey) {

                // 处理上级节点未展开的情况
                if (flatKeyTree[keyIndex - 1] &&
                    treeElsObj[flatKeyTree[keyIndex - 1]]?.props.pos.length >
                    treeElsObj[flatKeyTree[keyIndex]]?.props.pos.length) {

                    let parentTree;
                    if (flatKeyTree[keyIndex - 1]) {
                        const prePos = treeElsObj[flatKeyTree[keyIndex - 1]]?.props.pos.split('-');
                        const curPosLength = treeNode.props.pos.split('-').length;
                        for (let i = curPosLength - 1; i < prePos.length; i++) {
                            const parentPos = prePos.slice(0, i + 1).join('-');
                            parentTree = Object.values(treeElsObj).find((tree: any) => tree.props.pos === parentPos);
                            if (!expandedKeys?.includes(parentTree?.props.eventKey)) {
                                break;
                            }
                        }
                    }

                    if (!parentTree) {
                        betweenWithKeyRef.current = [flatKeyTree[keyIndex - 1], treeNode.props.eventKey];
                    } else {
                        betweenWithKeyRef.current = [parentTree.props.eventKey, treeNode.props.eventKey];
                    }
                } else {
                    betweenWithKeyRef.current = [flatKeyTree[keyIndex - 1], treeNode.props.eventKey];
                }
                dragXRef.current = e.pageX;
            }
            dropPositionYRef.current = betweenWithKeyRef.current[0] ? 2 : -1;
            setDropPositionY(dropPositionYRef.current);

            // 在第一个节点上面的时候失去插入位置
            if (dropPositionYRef.current === -1 && pageY < offsetTop) {
                betweenWithKeyRef.current = [];
                dropPositionYRef.current = undefined;
                setDropPositionY(undefined);
            }
        } else {
            // 覆盖在节点上的情况
            dropPositionYRef.current = 0;
            setDropPositionY(0);
            betweenWithKeyRef.current[0] = treeNode.props.eventKey;
            betweenWithKeyRef.current[1] = treeNode.props.eventKey;
            dragXRef.current = e.pageX;

            if (dragOverNodeKey === treeNode.props.eventKey) {
                if (!timerRef.current && !dragInitialState) {
                    const expandedKeysForDrag = getExpandedKeys(treeNode, true);
                    timerRef.current = setTimeout(() => {
                        if (expandedKeysForDrag) {
                            rawExpandedKeysRef.current = getRawExpandedKeys();
                            setExpandedKeys(expandedKeysForDrag);
                        }
                    }, dragDelayExpandTime);
                }
            }
        }
    }, [expandedKeys, dragOverNodeKey, dragInitialState, dragDelayExpandTime, getExpandedKeys, getRawExpandedKeys, setExpandedKeys]);

    /**
     * 处理拖拽进入
     */
    const handleDragEnter = useCallback((e: React.DragEvent, treeNode: React.ReactElement) => {
        e.preventDefault();
        e.stopPropagation();

        const expandedKeysForDrag = getExpandedKeys(treeNode, true);
        const posArr = treeNode.props.pos.split('-');

        onDragEnter?.({
            event: e,
            node: treeNode,
            expandedKeys: expandedKeysForDrag ? [...expandedKeysForDrag] : [...expandedKeys],
            dropPosition: (dropMap[dropPositionX] || 0) + Number(posArr[posArr.length - 1]),
        });
    }, [expandedKeys, dropPositionX, getExpandedKeys, onDragEnter]);

    /**
     * 处理拖拽覆盖
     */
    const handleDragOver = useCallback((e: React.DragEvent, treeNode: any, treeNodeContentWrapper?: any) => {
        e.preventDefault();
        e.stopPropagation();

        if (dropPositionX !== '' && dragXRef.current === null) {
            setDropPositionX('');
        }

        const staticNum = 20;

        const dragOverCallback = (statePosition: string, positionX?: string) => {
            const allowPositionX = onDragOver?.({
                event: e,
                node: treeNode,
                extension: {
                    dropPosition: statePosition,
                    dropPositionX: positionX || statePosition
                }
            });

            if (typeof allowPositionX === 'boolean') {
                return allowPositionX && setDropPositionX(positionX || statePosition);
            }

            if (!unsetDropPositionX) {
                setDropPositionX(statePosition);
            }
        };

        // 在父节点和第一个子节点时一直为right
        if (dropPositionYRef.current === 0) {
            dragOverCallback('right');
        } else if (dragXRef.current !== null && betweenWithKeyRef.current[0] && betweenWithKeyRef.current[1] &&
            treeElsObj[betweenWithKeyRef.current[1]]?.props.pos.length >
            treeElsObj[betweenWithKeyRef.current[0]]?.props.pos.length &&
            expandedKeys.includes(betweenWithKeyRef.current[0])) {
            dragOverCallback('right', 'center');
        } else if (dragXRef.current !== null && dropPositionX !== 'center' &&
            e.pageX >= dragXRef.current - staticNum && e.pageX <= dragXRef.current + staticNum) {
            dragOverCallback('center');
        } else if (dragXRef.current !== null && dropPositionX !== 'right' && e.pageX > dragXRef.current + staticNum) {
            dragOverCallback('right');
        } else if (dragXRef.current !== null && dropPositionX !== 'left' && e.pageX < dragXRef.current - staticNum) {
            if (betweenWithKeyRef.current[0] && betweenWithKeyRef.current[1] &&
                treeElsObj[betweenWithKeyRef.current[1]]?.props.pos.length <
                treeElsObj[betweenWithKeyRef.current[0]]?.props.pos.length) {
                const len = treeElsObj[betweenWithKeyRef.current[0]].props.pos.split('-').length -
                    treeElsObj[betweenWithKeyRef.current[1]].props.pos.split('-').length;
                for (let i = 0; i < len + 1; i++) {
                    if (i === len && e.pageX < dragXRef.current - i * staticNum) {
                        dragOverCallback(`left_${i}`);
                        break;
                    }
                    if (e.pageX < dragXRef.current - i * staticNum && e.pageX > dragXRef.current - (i + 1) * staticNum) {
                        dragOverCallback(`left_${i}`);
                        break;
                    }
                }
            }
        } else {
            dragOverCallback(dropPositionX || 'center');
        }

        // 构建带有 treeNodeContentWrapper 的 treeNode 对象
        const treeNodeWithWrapper = {
            ...treeNode,
            treeNodeContentWrapper: treeNodeContentWrapper
        };
        handleDragEnterGap(e, treeNodeWithWrapper);

        if (dragNodeRef.current?.props?.eventKey === treeNode.props.eventKey) {
            if (dragOverNodeKey === '') {
                return;
            }
            setDragOverNodeKey('');
            return;
        }

        if (betweenWithKeyRef.current[0] && dragOverNodeKey !== betweenWithKeyRef.current[0]) {
            setDragOverNodeKey(betweenWithKeyRef.current[0]);
        }

        return false;
    }, [dropPositionX, expandedKeys, dragOverNodeKey, unsetDropPositionX, onDragOver, handleDragEnterGap]);

    /**
     * 处理拖拽离开
     */
    const handleDragLeave = useCallback((e: React.DragEvent, treeNode: React.ReactElement) => {
        e.stopPropagation();
        onDragLeave?.({
            event: e,
            node: treeNode
        });
    }, [onDragLeave]);

    /**
     * 处理放置
     */
    const handleDrop = useCallback((e: React.DragEvent, treeNode: React.ReactElement) => {
        e.preventDefault();
        e.stopPropagation();

        const key = treeNode.props.eventKey;
        setDragOverNodeKey('');
        setDropNodeKey(key);
        setDropPositionX('');
        setDropPositionY(undefined);
        setDragNodeHighlight(false);

        if (dragNodesKeysRef.current.indexOf(key) > -1) {
            console.warn('can not drop to dragNode(include it\'s children node)');
            return;
        }

        let node = betweenWithKeyRef.current[0] === key ? treeNode : treeElsObj[betweenWithKeyRef.current[0]];
        let dropPosition = dropMap[dropPositionX] as number;

        // 在第一个元素的上面
        if (!betweenWithKeyRef.current[0]) {
            node = treeNode;
            dropPosition = -1;
        }

        // 处理左侧的层级
        if (dropPositionX.startsWith('left')) {
            const len = +dropPositionX.split('_')[1];
            const posArr = treeElsObj[betweenWithKeyRef.current[0]].props.pos.split('-');
            posArr.length = posArr.length - len;
            const nodePos = posArr.join('-');
            node = Object.values(treeElsObj).find((tree: any) => tree.props.pos === nodePos)!;
            dropPosition = 1;
        }

        const posArr = node.props.pos.split('-');
        const res: OnDropInfo = {
            event: e,
            node: node,
            dragNode: dragNodeRef.current!,
            dragNodesKeys: [...dragNodesKeysRef.current],
            dropPosition: dropPosition + Number(posArr[posArr.length - 1]),
        };

        if (dropMap[dropPositionX] !== 0) {
            res.dropToGap = true;
        }

        if ('expandedKeys' in props && rawExpandedKeysRef.current) {
            res.rawExpandedKeys = [...rawExpandedKeysRef.current];
        }

        onDrop?.(res);
        dropTriggerRef.current = true;

        // drop 后防止点击展开使expandedKeys延时设置结果错误
        if (timerRef.current) {
            clearTimeout(timerRef.current);
        }
    }, [dropPositionX, props, onDrop]);

    /**
     * 处理拖拽结束
     */
    const handleDragEnd = useCallback((e: React.DragEvent, treeNode: React.ReactElement) => {
        e.stopPropagation();
        setDragOverNodeKey('');
        setDropPositionX('');
        setDropPositionY(undefined);
        setDragNodeHighlight(false);
        dragNodesKeysRef.current = [];
        betweenWithKeyRef.current = [];
        dragXRef.current = null;
        dropPositionYRef.current = undefined;
        onDragEnd?.({
            event: e,
            node: treeNode
        });
    }, [onDragEnd]);

    return {
        dragOverNodeKey,
        dropNodeKey,
        dropPositionX,
        dropPositionY,
        dragNodeHighlight,
        handleDragStart,
        handleDragEnterGap,
        handleDragEnter,
        handleDragOver,
        handleDragLeave,
        handleDrop,
        handleDragEnd,
        getDragNodes
    };
}

// 辅助函数
function loopAllChildren(childs: React.ReactElement[], callback: Function): void {
    const loop = (children: React.ReactElement[], level: string | number) => {
        React.Children.forEach(children, (item: React.ReactElement, index) => {
            if (item) {
                const pos = `${level}-${index}`;
                callback(item, index, pos, item.key || pos);
                if (item.props.children) {
                    loop(item.props.children, pos);
                }
            }
        });
    };
    loop(childs, 0);
}